import React, { useState, useEffect } from 'react';
import Layout from '../../layouts/Layout';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { FaSearch, FaEye, FaChevronDown } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import Loader from '../components/Loader';
import { getOrderListAPI, updateOrderStatusAPI } from '../../services/api/OrderAPI';
import { Modal } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';

const Orders = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const user = useSelector((state) => state.LoginReducer.authData);
    const token = user.data.token;
    const [loading, setLoading] = useState(true);
    const [show, setShow] = useState(false);
    const [modalData, setModalData] = useState(null);

    const handleShow = (data) => {
        setModalData(data);
        setShow(true);        
        formik.setValues({
            status: data.status
        });
    };

    const handleClose = () => {
        setShow(false);
    };
    
    const onGlobalFilterChange = (e) => {
        setGlobalFilterValue(e.target.value);
    };

    useEffect(() => {
        fetchOrdersList();
    }, []);

    const fetchOrdersList = async () => {
        try {
            const data = await getOrderListAPI(token);            
            if(data.status){
                setOrders(data.data);
            }
        } catch (error) {
            console.error('Error fetching user list:', error);
        } finally {
            setLoading(false);
        }
    };

    const updateOrderStatus = async (formData) => {
        try {
            const id = modalData.id;
            const data = await updateOrderStatusAPI(formData, id, token);
            setLoading(false);
            if (data.status) {
                setShow(false);
                Swal.fire({
                    title: 'Order Status Updated!',
                    text: 'The order status has been Updated successfully.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                }).then(() => {
                    fetchOrdersList();
                });
            }
        } catch (error) {
            setLoading(false);
            Swal.fire({
                title: 'Error!',
                text: 'Something went wrong please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }
    };

    const validationSchema = Yup.object({
        status: Yup.string().required('Title is required')
    });
    
    const handleShowOrder = (id) => {
        navigate(`/orders/details/${id}`);
    };

    const formik = useFormik({
        initialValues: {
            status: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            const formData = new FormData();
            formData.append('status', values.status);
            await updateOrderStatus(formData);
        },
    });

    if (loading) {
        return <Loader />;
    }

    return (
        <Layout title="Orders">
            <div className="container table-container">
                <div className='d-flex gap-1 justify-content-end'>
                    <div className="mb-2 d-flex align-items-center custom-input-group">
                        <FaSearch className="search-icon" />
                        <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder="Search" className="flex-grow-1" />
                    </div>
                </div>
                <DataTable value={orders} paginator rows={10} sortField="name" sortOrder={1} removableSort stripedRows globalFilter={globalFilterValue} rowsPerPageOptions={[10, 25, 50, 100]} emptyMessage={<span className="custom-empty-message">No records found</span>} >
                    <Column field="id" header="#" sortable></Column>
                    <Column field="order_id" header="Order Id" sortable></Column>
                    <Column field="customar_name" header="Customar Name"></Column>
                    <Column field="total" header="Total" body={(rowData) => '$'+rowData.total} sortable />
                    <Column
                        field="status"
                        header="Status"
                        body={(rowData) => {{
                            let buttonClass;
                            switch (rowData.status) {
                                case 'Pending':
                                    buttonClass = 'btn-info';
                                    break;
                                case 'Processing':
                                    buttonClass = 'btn-secondary';
                                    break;
                                case 'Shipped':
                                    buttonClass = 'btn-warning';
                                    break;
                                case 'Delivered':
                                    buttonClass = 'btn-success';
                                    break;
                                case 'Cancelled':
                                    buttonClass = 'btn-danger';
                                    break;
                                default:
                                    buttonClass = 'btn-dark';
                            }
                            return <button className={`btn ${buttonClass} text-light status-btn`} variant="info" size="md" onClick={() => handleShow(rowData)}>{rowData.status}</button>;
                        }}}
                        sortable
                    />
                    <Column
                        field="status"
                        header="Action"
                        body={(rowData) => {{
                            return <div className='d-flex gap-2 align-items-center'>
                                <button className="btn btn-info text-light view" variant="info" size="md" onClick={() => handleShowOrder(rowData.id)}><FaEye /></button>
                            </div>
                        }}}
                        sortable
                    />
                </DataTable>
            </div>
            {/* Modal */}
            <Modal show={show} onHide={handleClose} size="md" backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Update Order Status</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                {modalData && (                
                    <form onSubmit={formik.handleSubmit}>
                        <input type="hidden" name="order_id" value={modalData.order_id} />
                        <div className="container">
                            <div className="row">
                                <div className="col-md-12 text-align-center">
                                    <h3>Order Id: {modalData.order_id}</h3>
                                </div>
                                <div className="col-md-12 mt-3 position-relative">
                                    <select name="status" id="status" className='form-control custom-select' value={formik.values.status} onChange={formik.handleChange} onBlur={formik.handleBlur}>
                                        <option value="">Select Status</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Processing">Processing</option>
                                        <option value="Shipped">Shipped</option>
                                        <option value="Delivered">Delivered</option>
                                        <option value="Cancelled">Cancelled</option>
                                    </select>
                                    <FaChevronDown className="custom-icon" />
                                    {formik.touched.status && formik.errors.status ? (<label className="text-danger">{formik.errors.status}</label>) : null}
                                </div>
                                <div className="col-md-12 mt-3 d-flex justify-content-center">
                                    <button type="submit" className="btn btn-primary">Update</button>
                                </div>
                            </div>
                        </div>
                    </form>
                )}
                </Modal.Body>
            </Modal>
        </Layout>
    )
}

export default Orders;